import React from 'react';
import { Box } from '@mui/material';
import { dietQualityColors, getDietQualityColor } from '../../lib/themes/generalTheme';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';

/**
 * @description Slider indicator component that shows the current score position
 * @param {Object} props
 * @param {number} props.score - Current score (1-10)
 * @param {boolean} props.isLimitNutrient - Whether this is a limit nutrient
 * @param {boolean} props.isGreen - Whether this is the green slider
 */
const SliderIndicator = ({ score, isLimitNutrient, isGreen }) => {
  // Calculate position first (0-100%)
  const position =
    score <= 5
      ? Math.max(5, Math.min(95, ((score - 1) / 4) * 100)) // Map 1-5 to 5-95%
      : Math.max(5, Math.min(95, ((score - 6) / 4) * 100)); // Map 6-10 to 5-95%

  // Calculate color based on position in the gradient
  let indicatorScore;
  if (isLimitNutrient) {
    if (isGreen) {
      // Green bar: dark green (9) -> light green (7)
      indicatorScore = 9 - Math.round((position / 100) * 2);
    } else {
      // Red bar: light red (5) -> dark red (1)
      indicatorScore = 5 - Math.round((position / 100) * 4);
    }
  } else {
    if (isGreen) {
      // Green bar: light green (7) -> dark green (9)
      indicatorScore = 7 + Math.round((position / 100) * 2);
    } else {
      // Red bar: dark red (1) -> light red (5)
      indicatorScore = 1 + Math.round((position / 100) * 4);
    }
  }

  const indicatorColor = getDietQualityColor(indicatorScore);

  return (
    <div
      style={{
        position: 'absolute',
        height: '20px',
        width: '8px',
        backgroundColor: indicatorColor,
        borderRadius: '8px',
        border: '2px solid #FFF',
        left: `${position}%`,
        transform: 'translateX(-50%)',
        top: '-6px',
        zIndex: 2,
        display: 'block',
      }}
    />
  );
};

/**
 * @description Goal location indicator component that shows the goal score position
 * @param {Object} props
 * @param {number} props.score - Goal score (1-10)
 * @param {boolean} props.isLimitNutrient - Whether this is a limit nutrient
 * @param {boolean} props.isGreen - Whether this is the green slider
 */
const GoalLocationIndicator = ({ score, isLimitNutrient, isGreen }) => {
  // Position based on goal score
  const position =
    score <= 5
      ? Math.max(5, Math.min(95, ((score - 1) / 4) * 100)) // Map 1-5 to 5-95%
      : Math.max(5, Math.min(95, ((score - 6) / 4) * 100)); // Map 6-10 to 5-95%

  // Always use green colors for the goal indicator
  const indicatorScore = 8; // Use consistent green color
  const indicatorColor = getDietQualityColor(indicatorScore);

  return (
    <div
      style={{
        position: 'absolute',
        left: `${position}%`,
        transform: 'translateX(-50%)',
        top: '-28px',
        zIndex: 3,
        display: 'flex',
        width: '24px',
        height: '24px',
        padding: '2px 4px 2.323px 4px',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <LocationOnRoundedIcon style={{ opacity: 0.6, color: indicatorColor, fontSize: '24px' }} />
    </div>
  );
};

/**
 * @description NutrientRangeSlider component that displays a gradient slider for diet quality scores
 *
 * The component can be used in two modes:
 * - Green mode (type='green'): Shows scores 6-10
 * - Red mode (type='red'): Shows scores 1-5
 *
 * The gradient direction depends on the nutrient type:
 * - For "limit" nutrients (added sugars, saturated fat, sodium, cholesterol):
 *   - Green slider: dark green → light green
 *   - Red slider: light yellow → dark red
 * - For all other nutrients (default):
 *   - Red slider: dark red → light yellow
 *   - Green slider: light green → dark green
 *
 * The goal indicator (green pin) appears above the green slider and shows the target value.
 * The vertical indicator bar shows the current value and matches the color at its position in the gradient.
 *
 * @param {Object} props - Component props
 * @param {number} props.currentValue - Current nutrient value
 * @param {number} props.goalValue - Goal/DV nutrient value
 * @param {boolean} [props.showIndicator=false] - Whether to show the current score indicator
 * @param {'green'|'red'} [props.type='green'] - Determines which gradient to show and behavior
 * @param {boolean} [props.isLimitNutrient=false] - Whether this is a "limit" nutrient
 * @param {number} [props.width=182] - Width of the slider
 * @param {string} [props.unit] - Unit of the nutrient
 */
const NutrientRangeSlider = ({
  currentValue,
  goalValue,
  showIndicator = false,
  type = 'green',
  isLimitNutrient = false,
  width = 117,
  name,
  unit,
}) => {
  // Hide the bar if there's no data
  if (!currentValue || !goalValue) {
    return null;
  }

  const isGreen = type === 'green';

  // Helper to get the max range for a nutrient based on its unit
  const getNutrientRange = (unit) => {
    const lowerUnit = (unit || '').toLowerCase();

    switch (lowerUnit) {
      case 'mcg':
        return 100; // 0-100mcg range
      case 'mg':
        return 4000; // 0-4000mg range
      case 'g':
        return 100; // 0-100g range
      default:
        return 100; // Default range if unit unknown
    }
  };

  // Calculate position based on value relative to goal
  const calculatePosition = (value) => {
    const maxRange = getNutrientRange(unit);

    // Calculate score from 1-10 based on value
    let score;
    if (isLimitNutrient) {
      // For limit nutrients, lower values are better
      // Map 0 to score 10, maxRange to score 1
      score = 10 - (value / maxRange) * 9;
    } else {
      // For non-limit nutrients, higher values are better
      // Map 0 to score 1, maxRange to score 10
      score = 1 + (value / maxRange) * 9;
    }
    score = Math.max(1, Math.min(10, Math.round(score)));

    // Convert score to position
    // Scores 1-5 map to red bar (0-100%)
    // Scores 6-10 map to green bar (0-100%)
    if (score <= 5) {
      // Red bar: score 1 = 0%, score 5 = 100%
      const position = ((score - 1) / 4) * 100;
      return { position, score };
    } else {
      // Green bar: score 6 = 0%, score 10 = 100%
      const position = ((score - 6) / 4) * 100;
      return { position, score };
    }
  };

  // Calculate positions and ensure they stay within bounds
  const { position: currentPosition, score: currentScore } = calculatePosition(currentValue);
  const boundedPosition = Math.max(0, Math.min(100, currentPosition));

  // For limit nutrients (added sugars, saturated fat, sodium, cholesterol)
  if (isLimitNutrient) {
    const startColor = isGreen
      ? dietQualityColors[8] // dark green (score 9)
      : dietQualityColors[4]; // light yellow (score 5)
    const endColor = isGreen
      ? dietQualityColors[6] // light green (score 7)
      : dietQualityColors[0]; // dark red (score 1)

    return (
      <div
        style={{
          position: 'relative',
          height: '8px',
          width: `${width}px`,
          borderRadius: '4px',
          backgroundColor: startColor,
          background: `linear-gradient(90deg, ${startColor} 0%, ${endColor} 100%)`,
          boxShadow: '0 0 0 1px rgba(0,0,0,0.05)',
          opacity: showIndicator ? 1 : 0.4,
          display: 'block',
          overflow: 'visible',
        }}
      >
        {showIndicator && (
          <SliderIndicator
            score={currentScore}
            isLimitNutrient={isLimitNutrient}
            isGreen={isGreen}
          />
        )}
      </div>
    );
  }

  // For all other nutrients (default)
  const startColor = isGreen
    ? dietQualityColors[6] // light green (score 7)
    : dietQualityColors[0]; // dark red (score 1)
  const endColor = isGreen
    ? dietQualityColors[8] // dark green (score 9)
    : dietQualityColors[4]; // light yellow (score 5)

  return (
    <div
      style={{
        position: 'relative',
        height: '8px',
        width: `${width}px`,
        borderRadius: '4px',
        backgroundColor: startColor,
        background: `linear-gradient(90deg, ${startColor} 0%, ${endColor} 100%)`,
        boxShadow: '0 0 0 1px rgba(0,0,0,0.05)',
        opacity: showIndicator ? 1 : 0.4,
        display: 'block',
        overflow: 'visible',
      }}
    >
      {showIndicator && (
        <SliderIndicator
          score={currentScore}
          isLimitNutrient={isLimitNutrient}
          isGreen={isGreen}
        />
      )}
    </div>
  );
};

export default NutrientRangeSlider;
